// FIXME
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type DatabaseWithoutEntries,
  DEFAULT_ENTRY_ID,
  fieldSettingsValidator,
  type ValueWithContext
} from '@databases/types/database';
import {
  type DatabaseCalculationType,
  DatabaseCoverFieldType,
  DatabaseLayoutType,
  type DatabaseView,
  type DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS
} from '@databases/types/database/view';
import { cloneDeep } from 'lodash-es';

import { DATABASE_CONTENT_TYPE, DEFAULT_VIEW_NAME } from '../constants.ts';
import { UnfoundEntryError } from '../errors.ts';
import { generateId } from '../utils.ts';

import { DATE_VALUE_TODAY, parseDatetime } from './field-types/date.ts';
import { getInitialValue } from './initial-values.ts';
import { getDatabaseFieldOptional, getDatabaseFieldRequired } from './predicates.ts';
import { getDefaultValuesForFilter } from './view/default-filter-values.ts';

export {
  getDatabaseFieldOptional,
  getDatabaseFieldRequired,
  isBasicDatabase,
  isBrokenDatabase,
  isNonBasicDatabase,
  isNonBrokenDatabase,
  isNonShallowDatabase,
  isNonShallowOrBrokenDatabase,
  isShallowDatabase
} from './predicates.ts';

/**
 * Create a new database entry that has the default values applied.
 */
export function createEntry<Mode extends ObjectMode.EXISTING | ObjectMode.CREATE>(
  database: Database<Mode>,
  viewSettings?: DatabaseViewSettings,
  { id, values }: { id?: string; values?: Record<string, DatabaseValue> } = {}
): DatabaseEntry {
  const filterDefaultValues =
    viewSettings && !values ? getDefaultValuesForFilter(viewSettings.filter) : {};
  const result: DatabaseEntry = {
    id: id ?? generateId(),
    ...('entriesContentId' in database
      ? {
          contentId: database.entriesContentId
        }
      : {}),
    values:
      values ??
      Object.fromEntries(
        database.fields.map((fieldId) => {
          const field = database.fieldsById[fieldId];
          let value;
          if (field?.settings?.defaultEnabled) {
            value = cloneDeep(
              getDefaultValue(database, getDatabaseFieldRequired(database, fieldId))
            );
            if (
              field.type === DatabaseFieldType.DATE &&
              value?.value === DATE_VALUE_TODAY &&
              field.settings.timeFormat
            ) {
              const resolvedToday = parseDatetime(DATE_VALUE_TODAY);
              if (resolvedToday !== undefined) {
                value = { ...value, value: resolvedToday };
              }
            }
          } else if (filterDefaultValues && fieldId in filterDefaultValues) {
            value = filterDefaultValues[fieldId];
          } else {
            value = getInitialValue(field.type);
          }
          return [fieldId, value];
        })
      )
  };

  return result as DatabaseEntry;
}

/**
 * Create a new database field with initial settings.
 */
export function createField<Type extends DatabaseFieldType>(
  type: Type,
  data: Partial<Omit<DatabaseField<Type>, 'type'>> = {}
): DatabaseField<Type> {
  return {
    id: generateId(),
    name: '',
    settings: fieldSettingsValidator.parse({}),
    ...data,
    type
  };
}

export function getNewDatabase({
  baseFields,
  entryCount = 3
}: {
  baseFields?: DatabaseField[];
  entryCount?: number;
} = {}): Database<ObjectMode.CREATE> {
  const databaseId = generateId();
  const defaultViewId = generateId();

  const fields: DatabaseField[] = baseFields ?? [
    {
      ...createField(DatabaseFieldType.TEXT),
      name: 'Text field'
    },
    (() => {
      const f = createField(DatabaseFieldType.SELECT);
      return {
        ...f,
        name: 'Tag field'
      };
    })(),
    {
      ...createField(DatabaseFieldType.USER),
      name: 'User field'
    }
  ];

  const entries = Array.from({ length: entryCount }, () => ({
    id: generateId(),
    values: {}
  }));

  return {
    id: databaseId,
    name: '',
    space: { key: '' },
    fields: fields.map((field) => field.id),
    fieldsById: Object.fromEntries(fields.map((field) => [field.id, field])),
    defaultEntry: {
      id: DEFAULT_ENTRY_ID,
      values: {}
    },
    entries: entries.map((entry) => entry.id),
    entriesById: Object.fromEntries(entries.map((entry) => [entry.id, entry])),
    coverImage: undefined,
    meta: {
      id: databaseId,
      isLocked: false
    },
    commentThreads: {},
    views: [
      {
        id: defaultViewId,
        name: DEFAULT_VIEW_NAME,
        settings: DEFAULT_DATABASE_VIEW_SETTINGS
      }
    ],
    defaultViewId: defaultViewId,
    type: DATABASE_CONTENT_TYPE
  };
}

export function getExistingDatabase({
  baseFields,
  contentId,
  databaseName,
  entryCount
}: {
  baseFields?: DatabaseField[];
  contentId: string;
  databaseName?: string;
  entryCount?: number;
}): Database<ObjectMode.EXISTING> {
  return {
    ...getNewDatabase({ baseFields, entryCount }),
    name: databaseName ?? `Existing database ${contentId}`,
    contentId,
    space: { key: 'TEST', name: 'Test', id: 1234 },
    operations: {
      delete: false,
      updateEntries: true,
      updateMetadata: true
    },
    contentVersion: 1,
    metaVersion: 0,
    entriesContentId: '',
    entriesContentVersion: 0,
    createdDate: '2025/04/28',
    createdBy: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: ''
      }
    },
    lastUpdatedDate: '2025/04/28',
    lastUpdatedBy: {
      type: 'anonymous',
      displayName: '',
      profilePicture: {
        path: ''
      }
    }
  };
}

/**
 * Returns an iterator that iterates through all database values (including field default values) of the database.
 */
export function* generateAllValues<Mode extends ObjectMode>(
  database: Database<Mode>
): Generator<ValueWithContext<DatabaseFieldType, Mode>, void, void> {
  for (const entryId of [database.defaultEntry.id, ...database.entries]) {
    const entry = getDatabaseEntryRequired(database, entryId);
    for (const fieldId of database.fields) {
      const field = getDatabaseFieldRequired(database, fieldId);
      yield { database, field, entry, value: getDatabaseValue(database, field, entry) };
    }
  }
}

/**
 * Returns the default value for the specified database field. Note that the value may have a different type
 * than the field, if the field type was changed at some point.
 */
export function getDefaultValue(
  database: Database<ObjectMode>,
  field: DatabaseField
): DatabaseValue {
  return getDatabaseValue(database, field, database.defaultEntry);
}

/**
 * Returns the database value that belongs to the specified field and entry. If the entry has no value for the
 * specified field, the default value of the field is returned. The returned value may have a different type
 * than the field, if the field type was changed at some point.
 */
export function getDatabaseValue(
  database: DatabaseWithoutEntries<ObjectMode>,
  field: DatabaseField,
  entry: DatabaseEntry
): DatabaseValue {
  if (Object.prototype.hasOwnProperty.call(entry.values, field.id)) {
    return entry.values[field.id];
  } else if (
    Object.prototype.hasOwnProperty.call(database.defaultEntry.values, field.id)
  ) {
    return cloneDeep(database.defaultEntry.values[field.id]);
  } else {
    return cloneDeep(getInitialValue(field.type));
  }
}

export {
  conversionCache,
  getConvertedDatabaseValue,
  getEntryName,
  getEntryPageReference,
  getNumberRepresentation
} from './converted-value-helpers.ts';

/**
 * Returns a string value that can be used to identify the given entry, in order to be listed in an entry picker.
 * The name is derived from the string representation of the value of the first field.
 * If the value is empty, undefined is returned and it's up to the caller
 * to display a localized fallback of the "Unnamed entry" string.
 */

/**
 * Returns all fields of the database, in order.
 */
export function getDatabaseFields(
  database: DatabaseWithoutEntries<ObjectMode>
): DatabaseField[] {
  return database.fields.map((fieldId) => getDatabaseFieldRequired(database, fieldId));
}

/**
 * Returns all field names of the database.
 */
export function getExistingFieldNames(
  database: Database<ObjectMode.EXISTING | ObjectMode.CREATE>,
  field?: DatabaseField
): string[] {
  return getDatabaseFields(database)
    .filter((f) => !field || f.id !== field.id)
    .map((f) => f.name);
}

/**
 * Returns the entry with the given ID of the database. If no obejct with that ID exists, undefined is returned.
 */
export function getDatabaseEntryOptional<Mode extends ObjectMode>(
  database: Database<Mode>,
  entryId: string
): DatabaseEntry | undefined {
  if (entryId === database.defaultEntry.id) {
    return database.defaultEntry;
  }
  return database.entriesById[entryId];
}

/**
 * Returns all entries of the database, in order.
 */
export function getDatabaseEntries<Mode extends ObjectMode>(
  database: Database<Mode>
): Array<DatabaseEntry> {
  return database.entries.map((entryId) => getDatabaseEntryRequired(database, entryId));
}

export function getFirstFieldId(database: Pick<Database, 'fields'>): string {
  return database.fields[0];
}

/**
 * Returns the first field of the database.
 */
export function getFirstField(
  database: DatabaseWithoutEntries<ObjectMode>
): DatabaseField | undefined {
  return getDatabaseFieldOptional(database, getFirstFieldId(database));
}

/**
 * Returns the cover field of the database. If no cover field is defined or the cover field is not an attachment/page/entry field, undefined is returned.
 * @param coverFieldId The cover field ID, or a DatabaseCoverFieldType value. Use NONE or DEFAULT for no cover. Defaults to DEFAULT.
 */
export function getCoverField(
  database: DatabaseWithoutEntries<ObjectMode>,
  coverFieldId: string | DatabaseCoverFieldType = DatabaseCoverFieldType.DEFAULT
): DatabaseField | undefined {
  if (
    coverFieldId === DatabaseCoverFieldType.DEFAULT ||
    coverFieldId === DatabaseCoverFieldType.NONE
  ) {
    return undefined;
  }

  const coverField = getDatabaseFieldOptional(database, coverFieldId);
  return coverField &&
    [
      DatabaseFieldType.ATTACHMENT,
      DatabaseFieldType.PAGE_LINK,
      DatabaseFieldType.ENTRY_LINK
    ].includes(coverField.type)
    ? coverField
    : undefined;
}

/**
 * Returns the fields of the database that should be rendered for a database card. If a cover field is selected for the
 * database card, it is excluded from the returned list of fields (if it is an attachment field). The group field is excluded
 * from the returned list of fields.
 * @param coverField The cover field configured for the database view
 * @param groupField The group field configured for the database view
 * @param subgroupField The subgroup field configured for the database view
 */
export function getVisibleFieldsForDatabaseCard(
  fields: DatabaseField[],
  coverField: DatabaseField | undefined,
  groupField: DatabaseField | undefined,
  subgroupField: DatabaseField | undefined
): DatabaseField[] {
  return (
    coverField
      ? fields.filter(
          (field) =>
            field.id !== coverField.id || field.type !== DatabaseFieldType.ATTACHMENT
        )
      : fields
  )
    .filter((field) => field.id !== groupField?.id)
    .filter((field) => field.id !== subgroupField?.id);
}

/**
 * Returns a reference to the entry page of the given database entry. The entry page is the page referenced by the first field
 * if the main field is a page field. If the entry does not have an entry page, undefined is returned.
 */

/**
 * Returns the entry with the given ID of the database. If no object with that ID exists, an error is thrown.
 * @throws An error if no such entry exists.
 */
export function getDatabaseEntryRequired<Mode extends ObjectMode>(
  database: Database<Mode>,
  entryId: string
): DatabaseEntry {
  const entry = getDatabaseEntryOptional(database, entryId);
  if (!entry) {
    throw new UnfoundEntryError(`Could not find database entry ${entryId}`, entryId);
  }
  return entry;
}

/**
 * Returns true if the field name is an empty string
 */
export function isUnnamedField(field: DatabaseField): boolean {
  return field.name === '';
}

export const viewHasCalculations = (view: DatabaseView): boolean => {
  return Boolean(
    view.settings.layout.type === DatabaseLayoutType.TABLE &&
    view.settings.calculations &&
    Object.keys(view.settings.calculations).length > 0
  );
};

export const fieldHasCalculations = (
  fieldId: string,
  calculations?: Record<string, DatabaseCalculationType>
): boolean => {
  return Boolean(calculations && !!calculations[fieldId]);
};

export const getFieldCalculationType = (
  fieldId: string,
  calculations?: Record<string, DatabaseCalculationType>
): DatabaseCalculationType | undefined => {
  return calculations && fieldHasCalculations(fieldId, calculations)
    ? calculations[fieldId]
    : undefined;
};

export const blurActiveElement = () =>
  // eslint-disable-next-line no-restricted-globals
  (window.document.activeElement as HTMLElement)?.blur?.();

export const removeTextSelection = () =>
  // eslint-disable-next-line no-restricted-globals
  window.document.getSelection()?.removeAllRanges();

export const fieldShouldSaveOnBlur = (fieldType: DatabaseFieldType): boolean => {
  return fieldType === DatabaseFieldType.TEXT;
};
